import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { Clock } from 'lucide-react';
import { DEFAULT_PROFILE_IMAGE } from '@/constants/defaultImages';
import type { VolunteerInfo, ProfileEvent } from './types';
import { formatDuration } from '@/utils/volunteerHours';

interface EventPreviewVolunteerSectionProps {
  loading: boolean;
  isOrganization: boolean;
  isEventEnded: boolean;
  isAlreadyVolunteer: boolean;
  isAcceptedVolunteer: boolean;
  volunteerInfo: VolunteerInfo | null;
  incompleteTasksCount: number;
  currentEvent: ProfileEvent | null;
  onOpenVolunteerTasks?: (eventId: string) => void;
  onOpenAddHours: () => void;
  onCloseModal: () => void;
}

export function EventPreviewVolunteerSection({
  loading,
  isOrganization,
  isEventEnded,
  isAlreadyVolunteer,
  isAcceptedVolunteer,
  volunteerInfo,
  incompleteTasksCount,
  currentEvent,
  onOpenVolunteerTasks,
  onOpenAddHours,
  onCloseModal,
}: EventPreviewVolunteerSectionProps) {
  if (loading && !isOrganization && !isEventEnded) {
    return (
      <Box px={{ base: '5', md: '0' }} mb="4">
        <Flex
          bg="rgba(31,41,50,0.05)"
          borderRadius="xl"
          p="4"
          align="center"
          gap="3"
          border="1px solid"
          borderColor="rgba(31,41,50,0.1)"
          className="animate-pulse"
        >
          <Box w="12" h="12" borderRadius="full" bg="volyx.lightGray" flexShrink={0} />
          <Box flex="1" minW="0" css={{ '& > * + *': { marginTop: '8px' } }}>
            <Box h="4" bg="volyx.lightGray" borderRadius="md" w="50%" />
            <Box h="3" bg="volyx.lightGray" borderRadius="md" w="75%" />
            <Box h="3" bg="volyx.lightGray" borderRadius="md" w="66%" />
          </Box>
        </Flex>
      </Box>
    );
  }

  if (!loading && !isOrganization && isAlreadyVolunteer && volunteerInfo && !isEventEnded) {
    return (
      <Box px={{ base: '5', md: '0' }} mb="4">
        <Flex
          bg="#F2F4F7"
          borderRadius="xl"
          p="4"
          align="center"
          gap="3"
          border="1px solid"
          borderColor="rgba(200,200,200,0.4)"
        >
          <chakra.img
            src={volunteerInfo.profile_image || DEFAULT_PROFILE_IMAGE}
            alt={`${volunteerInfo.first_name} ${volunteerInfo.last_name}`}
            w="12"
            h="12"
            borderRadius="full"
            flexShrink={0}
            objectFit="cover"
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              const target = e.target as HTMLImageElement;
              if (target.src !== DEFAULT_PROFILE_IMAGE) target.src = DEFAULT_PROFILE_IMAGE;
            }}
          />
          <Box flex="1" minW="0">
            <Text fontSize="md" fontWeight="bold" color="volyx.text" mb="1.5">
              {volunteerInfo.first_name} {volunteerInfo.last_name}
            </Text>
            <Box css={{ '& > * + *': { marginTop: '4px' } }}>
              <Flex align="center" gap="1.5">
                <Text as="span" fontSize="xs" color="volyx.textSecondary" fontWeight="medium">Department:</Text>
                <Text as="span" fontSize="xs" color="volyx.text" fontWeight="semibold" lineClamp={2}>{volunteerInfo.department}</Text>
              </Flex>
              <Flex align="center" gap="1.5">
                <Text as="span" fontSize="xs" color="volyx.textSecondary" fontWeight="medium">Hours:</Text>
                <Text as="span" fontSize="xs" color="volyx.text" fontWeight="semibold">{formatDuration(volunteerInfo.hours)}</Text>
              </Flex>
            </Box>
          </Box>
        </Flex>

        <Flex px={{ base: '5', md: '0' }} gap="3" mt="2">
          <chakra.button
            disabled={!currentEvent?.id}
            onClick={() => {
              if (!currentEvent?.id || !onOpenVolunteerTasks) return;
              onCloseModal();
              setTimeout(() => onOpenVolunteerTasks(currentEvent.id), 300);
            }}
            flex="1"
            borderRadius="2xl"
            py="3.5"
            px="5"
            border="1px solid"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="2"
            fontSize="md"
            fontWeight="semibold"
            boxShadow="0 2px 8px rgba(0,0,0,0.15)"
            bg={currentEvent?.id ? '#007AFF' : 'volyx.lightGray'}
            borderColor={currentEvent?.id ? '#007AFF' : 'volyx.mediumGray'}
            color={currentEvent?.id ? 'white' : 'volyx.textSecondary'}
            opacity={currentEvent?.id ? 1 : 0.7}
            cursor={currentEvent?.id ? 'pointer' : 'not-allowed'}
          >
            {incompleteTasksCount > 0 && (
              <Flex
                as="span"
                display="inline-flex"
                align="center"
                justify="center"
                minW="5"
                h="5"
                borderRadius="full"
                bg="whiteAlpha.200"
                px="1.5"
              >
                <Text as="span" fontSize="xs" fontWeight="bold" color="white">
                  {incompleteTasksCount > 99 ? '99+' : incompleteTasksCount}
                </Text>
              </Flex>
            )}
            <Text as="span">Tasks</Text>
          </chakra.button>
          <chakra.button
            disabled={!isAcceptedVolunteer}
            onClick={() => {
              if (!isAcceptedVolunteer) return;
              onOpenAddHours();
            }}
            flex="1"
            borderRadius="2xl"
            py="3.5"
            px="5"
            border="1px solid"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="2"
            fontSize="md"
            fontWeight="semibold"
            boxShadow="0 2px 8px rgba(0,0,0,0.15)"
            bg={isAcceptedVolunteer ? '#007AFF' : 'volyx.lightGray'}
            borderColor={isAcceptedVolunteer ? '#007AFF' : 'volyx.mediumGray'}
            color={isAcceptedVolunteer ? 'white' : 'volyx.textSecondary'}
            opacity={isAcceptedVolunteer ? 1 : 0.7}
            cursor={isAcceptedVolunteer ? 'pointer' : 'not-allowed'}
          >
            <Clock size={18} />
            <Text as="span">Add hours</Text>
          </chakra.button>
        </Flex>
      </Box>
    );
  }

  return null;
}
